import { useEffect, useState } from 'react';
import {
  COUNTRIES,
  INTRO_COPY,
  OTHER_COUNTRIES,
  PRIORITY_COUNTRIES,
  US_STATES,
} from '../constants/formConfig';
import { checkSchoolCode } from '../services/registrationApi';
import { Field } from './Field';

const SCHOOL_CODE_RE = /^[A-Za-z]{3,5}$/;

export function SchoolInformation({ school, errors, onChange, onCodeConflict }) {
  const update = (key, value) => {
    onChange({ ...school, [key]: value });
  };

  /**
   * Saved drafts may still hold a country that is no longer offered (e.g. the
   * old "Other" option). Surface it so the select shows the stored value
   * instead of silently displaying a different one.
   */
  const isKnownCountry = COUNTRIES.includes(school.country);

  // Live School Code availability check (debounced). The submit-time guard on
  // the API is the authoritative check; this is an early warning for the user.
  const [codeStatus, setCodeStatus] = useState(null);
  const code = school.schoolCode.trim().toUpperCase();
  const acsiId = school.acsiId.trim();
  const validCodeFormat = SCHOOL_CODE_RE.test(code);

  useEffect(() => {
    if (!validCodeFormat) {
      setCodeStatus(null);
      onCodeConflict?.(false);
      return undefined;
    }

    let cancelled = false;
    setCodeStatus({ state: 'checking' });

    const timer = setTimeout(async () => {
      const result = await checkSchoolCode(code, acsiId);
      if (cancelled) return;

      if (result.skipped) {
        setCodeStatus(null);
        onCodeConflict?.(false);
      } else if (result.available) {
        setCodeStatus({ state: result.sameSchool ? 'sameSchool' : 'available' });
        onCodeConflict?.(false);
      } else {
        setCodeStatus({ state: result.needsAcsi ? 'needsAcsi' : 'taken' });
        onCodeConflict?.(!result.needsAcsi);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [code, acsiId, validCodeFormat, onCodeConflict]);

  return (
    <section className="section">
      <h2 className="section-title">School Information</h2>

      <div className="field-grid two">
        <Field label="School Name" required error={errors.schoolName}>
          <input
            type="text"
            value={school.name}
            onChange={(e) => update('name', e.target.value)}
            autoComplete="organization"
          />
        </Field>

        <Field label="ACSI ID #" required error={errors.acsiId}>
          <input
            type="text"
            value={school.acsiId}
            onChange={(e) => update('acsiId', e.target.value)}
          />
          <p className="field-help">{INTRO_COPY.acsiHelp}</p>
        </Field>
      </div>

      <div className="field-grid" style={{ marginTop: 14 }}>
        <Field label="Street Address" required error={errors.address1}>
          <input
            type="text"
            value={school.address1}
            onChange={(e) => update('address1', e.target.value)}
            autoComplete="address-line1"
            placeholder="Street Address"
          />
        </Field>

        <Field label="Address Line 2">
          <input
            type="text"
            value={school.address2}
            onChange={(e) => update('address2', e.target.value)}
            autoComplete="address-line2"
            placeholder="Address Line 2"
          />
        </Field>
      </div>

      <div className="field-grid address-row" style={{ marginTop: 14 }}>
        <Field label="City" required error={errors.city}>
          <input
            type="text"
            value={school.city}
            onChange={(e) => update('city', e.target.value)}
            autoComplete="address-level2"
            placeholder="City"
          />
        </Field>

        <Field label="State / Region / Province" required error={errors.state}>
          {school.country === 'United States' ? (
            <select
              value={school.state}
              onChange={(e) => update('state', e.target.value)}
            >
              <option value="">Select</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={school.state}
              onChange={(e) => update('state', e.target.value)}
              autoComplete="address-level1"
            />
          )}
        </Field>

        <Field label="Postal / Zip Code" required error={errors.zip}>
          <input
            type="text"
            value={school.zip}
            onChange={(e) => update('zip', e.target.value)}
            autoComplete="postal-code"
          />
        </Field>
      </div>

      <div className="field-grid two" style={{ marginTop: 14 }}>
        <Field label="Country" required error={errors.country}>
          <select
            value={school.country}
            onChange={(e) => update('country', e.target.value)}
          >
            {!isKnownCountry ? (
              <option value={school.country}>
                {school.country || 'Select a country'}
              </option>
            ) : null}
            <optgroup label="Common">
              {PRIORITY_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </optgroup>
            <optgroup label="All countries">
              {OTHER_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </optgroup>
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 14 }}>
        <Field label="School Code" required error={errors.schoolCode}>
          <input
            type="text"
            value={school.schoolCode}
            onChange={(e) =>
              update('schoolCode', e.target.value.replace(/[^A-Za-z]/g, ''))
            }
            placeholder="e.g. ABC"
            maxLength={5}
          />
          {codeStatus?.state === 'checking' ? (
            <p className="field-help">Checking code availability…</p>
          ) : null}
          {codeStatus?.state === 'available' ? (
            <p className="code-status ok">✓ This code is available.</p>
          ) : null}
          {codeStatus?.state === 'sameSchool' ? (
            <p className="code-status ok">
              ✓ This matches your school’s existing registration — its details
              will be updated.
            </p>
          ) : null}
          {codeStatus?.state === 'taken' ? (
            <p className="code-status error">
              This school code is already registered to a different school.
              Please choose a different code.
            </p>
          ) : null}
          {codeStatus?.state === 'needsAcsi' ? (
            <p className="code-status warn">
              This code is already registered. If it’s your school, enter your
              ACSI ID above to confirm.
            </p>
          ) : null}
          <p className="field-help">{INTRO_COPY.schoolCodeHelp}</p>
        </Field>
      </div>
    </section>
  );
}
